import _ from 'lodash'
import type {ComponentWithResolvedDataItems} from '@wix/document-services-types'
import {isInnerElementsMapWrapper} from '../innerElementsMap/common'
import {COMPONENT_FEATURES} from '@wix/format-converter-schemas'

const INNER_ELEMENT_TYPE = 'InnerElement'
const INNER_ELEMENTS_FEATURE = COMPONENT_FEATURES.innerElements

interface InnerElementVariantContext {
    element: Record<string, unknown>
    componentId: string
}

type InnerElementVariantTransform = (ctx: InnerElementVariantContext) => void

/**
 * Unwrap InnerElementsMap wrapper if present
 * Returns the actual innerElements object, handling both wrapped and unwrapped formats
 */
const unwrapInnerElements = (innerElements: unknown): Record<string, unknown> | null => {
    if (!innerElements || !_.isPlainObject(innerElements)) {
        return null
    }

    if (isInnerElementsMapWrapper(innerElements)) {
        return (innerElements as {innerElements: Record<string, unknown>}).innerElements
    }

    return innerElements as Record<string, unknown>
}

/**
 * Get nested innerElements from an element
 * Checks both query name (innerElementsQuery) and alias name (innerElements)
 * Handles InnerElementsMap wrappers
 */
const getNestedInnerElements = (element: Record<string, unknown>): Record<string, unknown> | null => {
    const {query, alias} = INNER_ELEMENTS_FEATURE

    // Check alias first (after beautifier runs, this is the common case)
    const aliasValue = element[alias]
    if (aliasValue && _.isPlainObject(aliasValue)) {
        return unwrapInnerElements(aliasValue)
    }

    // Fall back to query name (for robustness)
    const queryValue = element[query]
    if (queryValue && _.isPlainObject(queryValue)) {
        return unwrapInnerElements(queryValue)
    }

    return null
}

/**
 * Recursively process innerElements and call transformation for each
 */
const processInnerElements = (
    innerElements: Record<string, unknown>,
    componentId: string,
    onInnerElement: InnerElementVariantTransform
): void => {
    for (const key of Object.keys(innerElements)) {
        const element = innerElements[key] as Record<string, unknown>
        if (!element || !_.isPlainObject(element)) {
            continue
        }

        // Process this innerElement if it's of type InnerElement
        if (element.type === INNER_ELEMENT_TYPE) {
            onInnerElement({element, componentId})
        }

        // Recursively process nested innerElements
        const nested = getNestedInnerElements(element)
        if (nested) {
            processInnerElements(nested, componentId, onInnerElement)
        }
    }
}

/**
 * Traverse component innerElements and apply variant transformations
 * Handles InnerElementsMap wrappers since variantsPlugin runs before innerElementsMapPlugin
 */
export const traverseInnerElementsForVariants = (
    component: ComponentWithResolvedDataItems,
    onInnerElement: InnerElementVariantTransform
): void => {
    const componentId = component.structure?.id as string
    if (!componentId) {
        return
    }

    const innerElements = unwrapInnerElements(component.data?.innerElements)
    if (!innerElements) {
        return
    }

    processInnerElements(innerElements, componentId, onInnerElement)
}
